'use client';

import type { PropsWithChildren} from 'react';
import { createContext, useContext } from 'react';

import { merge } from 'lodash';
import type { StoreApi} from 'zustand';
import { createStore, useStore } from 'zustand';

import type { NonFunctionProperties } from '@utilities/helpers/types';

import type {
  SwapOperationSide,
  SwapOperationSpecification} from '../types';
import {
  SwapOperationSpecificationSchema,
} from '../types';

interface SwapExperienceStoreData {
  source: Partial<SwapOperationSide>;
  destination: Partial<SwapOperationSide>;
  slippagePercent: number;
}

interface SwapExperienceStore extends SwapExperienceStoreData {
  getSource(): Partial<SwapOperationSide>;
  updateSource(updates: Partial<SwapOperationSide>): void;
  updateDestination(updates: Partial<SwapOperationSide>): void;
  setSlippagePercent(value: number): void;
  exportOperationSpecification(): SwapOperationSpecification;
  reset(parameters: SwapExperienceStoreResetParameters): void;
  resetAmounts(): void;
}

type SwapExperiencePreset = Partial<NonFunctionProperties<SwapExperienceStore>>;

const SwapExperienceStoreContext = createContext<StoreApi<SwapExperienceStore> | null>(null);

interface SwapExperienceStoreContextProviderProps {
  defaults: SwapExperiencePreset;
}

type SwapExperienceStoreResetParameters = {
  [K in keyof SwapExperienceStoreData]?: boolean;
};

const SwapExperienceStoreContextProvider = ({
  defaults: { source = {}, destination = {}, slippagePercent = 0 },
  children,
}: PropsWithChildren<SwapExperienceStoreContextProviderProps>) => {
  const defaults = { source, destination, slippagePercent };

  const store = createStore<SwapExperienceStore>((set, get) => ({
    source,
    destination,
    slippagePercent,
    getSource: () => {
      return get().source;
    },
    updateSource: (updates: Partial<SwapOperationSide>) => {
      set({ source: merge({}, get().source, updates) });
    },
    updateDestination: (updates: Partial<SwapOperationSide>) => {
      set({ destination: merge({}, get().destination, updates) });
    },
    setSlippagePercent: (value) => {
      set({ slippagePercent: value });
    },
    exportOperationSpecification: () => {
      return SwapOperationSpecificationSchema.parse(get());
    },
    reset: (parameters) => {
      const updates: Array<Partial<SwapExperienceStore>> = [];

      let key: keyof typeof defaults;

      for (key in defaults) {
        if (parameters[key]) {
          updates.push({ [key]: defaults[key] });
        }
      }

      set(merge({}, ...updates));
    },
    resetAmounts: () => {
      const { source, destination } = get();

      set({
        source: {
          ...source,
          amount: 0n,
        },
        destination: {
          ...destination,
          amount: 0n,
        },
      });
    },
  }));

  return (
    <SwapExperienceStoreContext.Provider value={store}>
      {children}
    </SwapExperienceStoreContext.Provider>
  );
};

const useSwapExperienceStore = () => {
  const store = useContext(SwapExperienceStoreContext);

  if (!store) {
    throw new Error(`SwapExperienceStoreContext is not available.`);
  }

  return useStore(store);
};

export {
  SwapExperienceStoreContextProvider,
  useSwapExperienceStore,
  type SwapExperienceStore,
  type SwapOperationSide,
  type SwapExperiencePreset,
};
